import { muiTheme } from "@/styles/theme";
import { Box, Button, CircularProgress, SxProps } from "@mui/material";
import { useEffect, useState } from "react";

const ModalFooter = ({
  handleClose,
  handleConfirm,
  confirmText,
  cancelText,
  isSubmitting,
  disabledConfirmButton,
  enableEnterKeySubmit = false,
  isForm = true,
  clearButton,
  showCancelButton = true,
  applyButton,
  cancelButton,
  cancelButtonSx,
  applyButtonSx,
  confirmTestId,
  cancelTestId,
}: {
  handleClose: () => void;
  handleConfirm?: () => void;
  confirmText?: string;
  cancelText?: string;
  confirmTestId?: string;
  cancelTestId?: string;
  isSubmitting?: boolean;
  disabledConfirmButton?: boolean;
  enableEnterKeySubmit?: boolean;
  isForm?: boolean;
  clearButton?: JSX.Element;
  showCancelButton?: boolean;
  applyButton?: JSX.Element;
  cancelButton?: JSX.Element;
  cancelButtonSx?: SxProps;
  applyButtonSx?: SxProps;
}) => {
  const [isFrozen, setIsFrozen] = useState(false);

  useEffect(() => {
    if (!enableEnterKeySubmit) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter" && !isFrozen && handleConfirm) {
        handleConfirm();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFrozen, handleConfirm, enableEnterKeySubmit]);

  useEffect(() => {
    if (isSubmitting) {
      setIsFrozen(true);
      setTimeout(() => {
        setIsFrozen(false);
      }, 1000);
    }
  }, [isSubmitting]);

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        justifyContent: showCancelButton ? "space-between" : "flex-end",
        alignItems: "center",
        width: "100%",
      }}
    >
      {clearButton ? (
        <Box onClick={handleClose}> {clearButton}</Box>
      ) : (
        showCancelButton &&
        (cancelButton ? (
          cancelButton
        ) : (
          <Button
            type="button"
            sx={{
              textTransform: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              alignItems: "center",
              justifyContent: "center",
              width: "120px",
              color: muiTheme.colorSchemes.light.palette.main.black,
              backgroundColor:
                muiTheme.colorSchemes.light.palette.background.white,
              border: "1px solid  #D0D5DD",
              ...muiTheme.typography.h4_med,

              ...cancelButtonSx,
            }}
            onClick={handleClose}
            data-testid={cancelTestId}
          >
            {cancelText ? cancelText : "Cancel"}
          </Button>
        ))
      )}

      <Box
        sx={{
          width: "fit-content",
        }}
      >
        {applyButton ? (
          applyButton
        ) : (
          <Button
            variant="contained"
            color="primary"
            sx={{
              textTransform: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              alignItems: "center",
              justifyContent: "center",
              width: "120px",
              minWidth: "fit-content",
              maxWidth: "120px",
              whiteSpace: "nowrap",
              ...muiTheme.typography.h4_med,
              ":disabled": {
                backgroundColor: "primary",
              },
              ...applyButtonSx,
            }}
            type={isForm ? "submit" : "button"}
            onClick={handleConfirm}
            disabled={isSubmitting || disabledConfirmButton}
            data-testid={confirmTestId}
          >
            {isSubmitting ? (
              <CircularProgress
                sx={{
                  maxHeight: "20px",
                  maxWidth: "20px",
                  color: muiTheme.colorSchemes.light.palette.main.white,
                }}
              />
            ) : (
              confirmText || "Confirm"
            )}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ModalFooter;
